import React, { useEffect, useState } from "react";
import { TopNav } from "@/components/TopNav";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { RankBadge } from "@/components/RankBadge";
import { getRank, nextRank, progressToNext } from "@/lib/ranks";
import { formatVND } from "@/lib/money";
import { Link, useNavigate } from "react-router-dom";
import { Wallet, Swords, MessagesSquare, Mail, Trophy, Crosshair, Gift, QrCode, ShieldCheck, ChevronRight, History, Plus, Tv, Users, Flame, Target, ShoppingBag } from "lucide-react";

const QuickAction = ({ to, icon: Icon, label, color = "#00e5ff", testid }) => (
  <Link to={to} data-testid={testid} className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-white/[0.04] transition group">
    <div className="w-12 h-12 rounded-2xl grid place-items-center border" style={{ borderColor: `${color}40`, background: `${color}1a` }}>
      <Icon size={20} style={{ color }} />
    </div>
    <span className="text-[11px] text-white/80 text-center leading-tight">{label}</span>
  </Link>
);

export default function PlayerHome() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [history, setHistory] = useState([]);
  const [arena, setArena] = useState([]);
  const [live, setLive] = useState([]);
  const [showBalance, setShowBalance] = useState(true);

  useEffect(() => {
    refresh();
    if (user?.id) api.get(`/matches?player_id=${user.id}&limit=5`).then(({ data }) => setHistory(data));
    api.get("/arena/requests").then(({ data }) => setArena(data));
    api.get("/matches/live").then(({ data }) => setLive(data));
    // eslint-disable-next-line
  }, []);

  if (!user) return null;
  const rank = getRank(user.elo);
  const nxt = nextRank(user.elo);
  const prog = progressToNext(user.elo);
  const verified = user.verification_status === "verified";

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        {/* Header: Avatar + Nickname + Rank */}
        <Link to="/profile" className="flex items-center gap-4 p-4 rounded-2xl bg-[#0c0d14] border border-white/8 hover:border-amber-400/30 transition" data-testid="home-profile-card">
          <div className="relative shrink-0">
            <div className="w-16 h-16 rounded-2xl overflow-hidden border-2" style={{ borderColor: rank.color }}>
              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
            </div>
            {verified && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#05050a] grid place-items-center border-2 border-cyan-400">
                <ShieldCheck size={11} className="text-cyan-300" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="font-display font-bold text-lg truncate">{user.nickname}</span>
              {verified && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-400/15 text-cyan-300 border border-cyan-400/30">VERIFIED</span>}
            </div>
            <RankBadge elo={user.elo} size="sm" />
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </Link>

        {/* Wallet card — bank style hero */}
        <div className="relative rounded-2xl overflow-hidden p-5 border border-amber-400/30" style={{ background: "linear-gradient(135deg, #1a1208 0%, #2a1c08 50%, #1a1208 100%)" }}>
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full blur-3xl bg-amber-500/20" />
          <div className="absolute inset-0 bg-grid opacity-20" />
          <div className="relative">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase tracking-widest text-amber-300/80">Số dư ví tiền</div>
              <button onClick={() => setShowBalance((s) => !s)} className="text-xs text-amber-300/60 hover:text-amber-300">
                {showBalance ? "Ẩn" : "Hiện"}
              </button>
            </div>
            <div className="font-display font-black text-4xl text-amber-300 mb-4" data-testid="home-balance">
              {showBalance ? formatVND(user.coin) : "•••••••đ"}
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[
                { to: "/wallet", icon: Plus, label: "Nạp tiền", color: "#22ee9c" },
                { to: "/wallet", icon: History, label: "Lịch sử", color: "#00e5ff" },
                { to: "/rewards", icon: Gift, label: "Đổi quà", color: "#ffb020" },
                { to: "/wallet", icon: Wallet, label: "Thanh toán", color: "#a855f7" },
              ].map((x) => (
                <Link key={x.label} to={x.to} className="flex flex-col items-center gap-1 py-2 rounded-lg bg-black/30 hover:bg-black/50 border border-white/5">
                  <x.icon size={16} style={{ color: x.color }} />
                  <span className="text-[10px] text-white/80">{x.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Rank progress */}
        {nxt.name !== rank.name && (
          <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-4">
            <div className="flex justify-between text-xs text-white/60 mb-2">
              <span style={{ color: rank.color }}>{rank.name} · {user.elo}</span>
              <span className="text-white/40">Còn {nxt.min - user.elo} ELO tới <span style={{ color: nxt.color }}>{nxt.name}</span></span>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${prog}%`, background: `linear-gradient(90deg, ${rank.color}, ${nxt.color})` }} />
            </div>
          </div>
        )}

        {/* Quick actions grid — bank-app style */}
        <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-3">
          <div className="grid grid-cols-4 gap-1">
            <QuickAction to="/arena" icon={Swords} label="Đấu trường" color="#ff2a4d" testid="qa-arena" />
            <QuickAction to="/point-counter" icon={Crosshair} label="Bắn đền" color="#ffb020" testid="qa-point" />
            <QuickAction to="/community" icon={MessagesSquare} label="Chat tổng" color="#a855f7" testid="qa-chat" />
            <QuickAction to="/chat" icon={Mail} label="Tin nhắn" color="#00e5ff" testid="qa-dm" />
            <QuickAction to="/leaderboard" icon={Trophy} label="Xếp hạng" color="#facc15" testid="qa-lb" />
            <QuickAction to="/shop" icon={ShoppingBag} label="Cửa hàng" color="#f472b6" testid="qa-shop" />
            <QuickAction to="/rewards" icon={Gift} label="Đổi quà" color="#22ee9c" testid="qa-rewards" />
            <QuickAction to="/verify" icon={ShieldCheck} label="Xác thực" color="#38bdf8" testid="qa-verify" />
          </div>
        </div>

        {/* Arena status pill */}
        <div className="grid grid-cols-2 gap-3">
          <Link to="/arena" className="rounded-2xl bg-gradient-to-br from-red-500/10 to-rose-700/5 border border-red-500/25 p-4 hover:border-red-500/50 transition">
            <div className="flex items-center justify-between mb-1">
              <Target size={16} className="text-red-300" />
              <span className="font-display font-black text-2xl text-red-300" data-testid="arena-count">{arena.length}</span>
            </div>
            <div className="text-xs text-white/70">Đang tìm đối thủ</div>
            <div className="text-[10px] text-red-300/60 mt-1">Vào xem ↗</div>
          </Link>
          <Link to="/arena" className="rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-700/5 border border-cyan-400/25 p-4 hover:border-cyan-400/50 transition">
            <div className="flex items-center justify-between mb-1">
              <Swords size={16} className="text-cyan-300" />
              <span className="font-display font-black text-2xl text-cyan-300" data-testid="live-count">{live.length}</span>
            </div>
            <div className="text-xs text-white/70">Trận đang diễn ra</div>
            <div className="text-[10px] text-cyan-300/60 mt-1">Xem live ↗</div>
          </Link>
        </div>

        {/* Recent history */}
        <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-sm uppercase tracking-wider flex items-center gap-2"><History size={13} className="text-cyan-300" /> Trận gần đây</h2>
            <Link to="/profile" className="text-xs text-cyan-300">Tất cả →</Link>
          </div>
          {history.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">Chưa có trận đấu</div>
          ) : (
            <div className="space-y-1.5">
              {history.map((m) => {
                const isA = m.player_a_id === user.id;
                const myDelta = isA ? m.elo_delta_a : m.elo_delta_b;
                const opp = isA ? m.player_b_nickname : m.player_a_nickname;
                const won = m.winner_id === user.id;
                return (
                  <div key={m.id} className="flex items-center gap-3 p-2.5 rounded-md bg-white/[0.02] border border-white/5">
                    <div className={`w-1 h-9 rounded-full ${won ? "bg-emerald-400" : "bg-red-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">vs {opp}</div>
                      <div className="text-[10px] text-white/40">Bàn {m.table_number || "—"} · BO{m.best_of} · {m.mode}</div>
                    </div>
                    <div className="text-sm font-mono-num">{isA ? m.score_a : m.score_b}-{isA ? m.score_b : m.score_a}</div>
                    <div className={`text-sm font-mono-num font-semibold ${myDelta >= 0 ? "text-emerald-300" : "text-red-400"}`}>{myDelta >= 0 ? "+" : ""}{myDelta}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
